import React, { useMemo, useState } from 'react';
import { buildTemplatesZip, itemsForDownload, saveBlob, zipNameFor } from '../utils/tvecDownload';
import { formatSize } from '../utils/tvecTemplates';

// "Download all" for the selected course: a ZIP that keeps the original folder structure.
export default function TvecDownloadAll({ course, className = '' }) {
  const items = useMemo(() => itemsForDownload(course), [course]);
  const bytes = useMemo(() => items.reduce((n, it) => n + (it.size || 0), 0), [items]);
  const [progress, setProgress] = useState(null); // null = idle, otherwise { done, total }
  const [error, setError] = useState('');

  const working = progress !== null;

  const run = async () => {
    if (working || items.length === 0) return;
    setError('');
    setProgress({ done: 0, total: items.length });
    try {
      const blob = await buildTemplatesZip(items, (done, total) => setProgress({ done, total }));
      saveBlob(blob, zipNameFor(course));
    } catch (err) {
      setError(err.failed?.length
        ? `${err.message}: ${err.failed.slice(0, 3).join(', ')}${err.failed.length > 3 ? '…' : ''}. Please try again.`
        : 'Could not prepare the download. Please try again.');
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`}>
      <button
        type="button"
        onClick={run}
        disabled={working || items.length === 0}
        title={`Download a ZIP of ${items.length} files in folders, exactly as the originals`}
        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-sky-400"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
        </svg>
        {working
          ? `Preparing… ${progress.done}/${progress.total}`
          : `Download all (${items.length} files${bytes ? ` · ${formatSize(bytes)}` : ''})`}
      </button>
      {error ? <p className="max-w-xs text-right text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}
